import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface FormSubmitContextValue {
  registerSubmit: (submitFn: () => void) => void;
  submit: () => void;
  isFormReady: boolean;
  isDirty: boolean;
  updateDirtyState: (isDirty: boolean) => void;
}

const FormSubmitContext = createContext<FormSubmitContextValue | null>(null);

interface FormSubmitProviderProps {
  children: ReactNode;
}

export const FormSubmitProvider: React.FC<FormSubmitProviderProps> = ({
  children,
}) => {
  const submitFnRef = useRef<(() => void) | null>(null);
  const isFormReadyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  const registerSubmit = useCallback((submitFn: () => void) => {
    submitFnRef.current = submitFn;
    isFormReadyRef.current = true;
  }, []);

  const updateDirtyState = useCallback((newIsDirty: boolean) => {
    setIsDirty(newIsDirty);
  }, []);

  const submit = useCallback(() => {
    if (submitFnRef.current) {
      submitFnRef.current();
    }
  }, []);
  const contextValue = useMemo<FormSubmitContextValue>(
    () => ({
      registerSubmit,
      submit,
      updateDirtyState,
      get isFormReady() {
        return isFormReadyRef.current;
      },
      isDirty,
    }),
    [registerSubmit, submit, updateDirtyState, isDirty]
  );

  return (
    <FormSubmitContext.Provider value={contextValue}>
      {children}
    </FormSubmitContext.Provider>
  );
};

export const useFormSubmit = (): FormSubmitContextValue => {
  const context = useContext(FormSubmitContext);

  if (!context) {
    throw new Error("useFormSubmit must be used within a FormSubmitProvider");
  }

  return context;
};
